/**
 * MealPlanTemplateModal Component
 * Modal for creating new meal plans using existing ones as templates
 */

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { MealPlan } from "../services/api";
import { LLMMealPlanResultOutgoing, LLMMealPlanOutgoing } from "../types";
import { toTitleCase } from "../utils/textFormatting";

type MealPreference = "dislike" | "ignore" | "like";

interface MealItem {
    id: string;
    mealType: string;
    dayNumber: number;
    recipeName: string;
    preference: MealPreference;
    notes: string;
}

interface MealPlanTemplateModalProps {
    isOpen: boolean;
    onClose: () => void;
    mealPlan: MealPlan | null;
}

export const MealPlanTemplateModal: React.FC<MealPlanTemplateModalProps> = ({
    isOpen,
    onClose,
    mealPlan,
}) => {
    const navigate = useNavigate();
    const [meals, setMeals] = useState<MealItem[]>([]);
    const [masterState, setMasterState] = useState<MealPreference>("ignore");
    const [overallNotes, setOverallNotes] = useState("");

    // Extract meals from meal plan when it changes
    useEffect(() => {
        if (!mealPlan?.plan) {
            setMeals([]);
            return;
        }

        const mealPlanData = mealPlan.plan as LLMMealPlanResultOutgoing;
        const extractedMeals: MealItem[] = [];

        mealPlanData.mealPlanDays.forEach(
            (day: LLMMealPlanOutgoing, dayIndex: number) => {
                const mealTypes = [
                    "breakfast",
                    "lunch",
                    "dinner",
                    "snack",
                ] as const;

                mealTypes.forEach((mealType) => {
                    const mealSlot = day[mealType] as { recipes: any[] };
                    const recipes = mealSlot?.recipes || [];

                    recipes.forEach((recipe, recipeIndex) => {
                        extractedMeals.push({
                            id: `${dayIndex}-${mealType}-${recipeIndex}`,
                            mealType:
                                mealType.charAt(0).toUpperCase() +
                                mealType.slice(1),
                            dayNumber: dayIndex + 1,
                            recipeName: toTitleCase(
                                recipe.name || `Recipe ${recipeIndex + 1}`
                            ),
                            preference: "ignore",
                            notes: "",
                        });
                    });
                });
            }
        );

        setMeals(extractedMeals);
        setMasterState("ignore");
    }, [mealPlan]);

    const handleMealToggle = (mealId: string) => {
        setMeals((prev) =>
            prev.map((meal) => {
                if (meal.id === mealId) {
                    // Cycle through states: dislike -> ignore -> like -> dislike
                    const nextPreference: MealPreference =
                        meal.preference === "dislike"
                            ? "ignore"
                            : meal.preference === "ignore"
                            ? "like"
                            : "dislike";
                    return { ...meal, preference: nextPreference };
                }
                return meal;
            })
        );
    };

    const handleMealNotesChange = (mealId: string, notes: string) => {
        setMeals((prev) =>
            prev.map((meal) => (meal.id === mealId ? { ...meal, notes } : meal))
        );
    };

    const handleMasterToggle = () => {
        // Cycle through master states: ignore -> like -> dislike -> ignore
        const nextState: MealPreference =
            masterState === "ignore"
                ? "like"
                : masterState === "like"
                ? "dislike"
                : "ignore";

        setMasterState(nextState);
        setMeals((prev) =>
            prev.map((meal) => ({ ...meal, preference: nextState }))
        );
    };

    const generatePromptString = () => {
        const likedMeals = meals.filter((meal) => meal.preference === "like");
        const dislikedMeals = meals.filter(
            (meal) => meal.preference === "dislike"
        );

        let prompt =
            "Please generate a meal plan considering the following preferences from my previous meal plan:\n\n";

        // Add liked meals
        if (likedMeals.length > 0) {
            prompt += "MEALS I ENJOYED (please include similar options):\n";
            likedMeals.forEach((meal) => {
                prompt += `- ${meal.recipeName}`;
                if (meal.notes.trim()) {
                    prompt += ` - ${meal.notes.trim()}`;
                }
                prompt += "\n";
            });
            prompt += "\n";
        }

        // Add disliked meals
        if (dislikedMeals.length > 0) {
            prompt += "MEALS I DIDN'T ENJOY (please avoid similar options):\n";
            dislikedMeals.forEach((meal) => {
                prompt += `- ${meal.recipeName}`;
                if (meal.notes.trim()) {
                    prompt += ` - ${meal.notes.trim()}`;
                }
                prompt += "\n";
            });
            prompt += "\n";
        }

        // Add overall notes
        if (overallNotes.trim()) {
            prompt += `ADDITIONAL PREFERENCES:\n${overallNotes.trim()}\n\n`;
        }

        // Extract original user instructions from the generation prompt
        if (mealPlan?.generation_prompt) {
            const originalPrompt = mealPlan.generation_prompt;

            // Look for "User Instructions:" section in the original prompt
            const userInstructionsMatch = originalPrompt.match(
                /User Instructions:\s*([\s\S]*?)(?:\n\n|$)/
            );

            if (userInstructionsMatch && userInstructionsMatch[1]) {
                const userInstructions = userInstructionsMatch[1].trim();
                if (userInstructions) {
                    prompt += `ORIGINAL INSTRUCTIONS FROM PREVIOUS MEAL PLAN:\n${userInstructions}\n\n`;
                }
            }
        }

        // Add closing instruction
        if (likedMeals.length === 0 && dislikedMeals.length === 0) {
            prompt += "Please generate a fresh meal plan";
            if (overallNotes.trim()) {
                prompt += " based on the preferences above";
            }
            prompt += ".";
        } else {
            prompt +=
                "Please use this feedback to create a new meal plan that aligns with my preferences.";
        }

        // Add two newlines for user to add more info
        prompt += "\n\n";

        return prompt;
    };

    const handleSubmit = () => {
        const promptString = generatePromptString();

        // Navigate to generate tab with the prompt
        navigate("/meal-planner/generate", {
            state: {
                templatePrompt: promptString,
            },
        });

        onClose();
    };

    if (!isOpen) return null;

    // Helper function to get colors for tristate switch
    const getToggleColors = (preference: MealPreference) => {
        switch (preference) {
            case "dislike":
                return { bg: "#ef4444", knobPos: "2px" }; // Red, left
            case "ignore":
                return { bg: "#9ca3af", knobPos: "22px" }; // Gray, center
            case "like":
                return { bg: "#10b981", knobPos: "42px" }; // Green, right
        }
    };

    const getMasterToggleColors = () => getToggleColors(masterState);

    const getMasterToggleLabel = () => {
        switch (masterState) {
            case "dislike":
                return "Set All to Dislike";
            case "ignore":
                return "Set All to Ignore";
            case "like":
                return "Set All to Like";
        }
    };

    // Styles
    const overlayStyle: React.CSSProperties = {
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 1000,
    };

    const modalStyle: React.CSSProperties = {
        backgroundColor: "white",
        borderRadius: "12px",
        width: "90%",
        maxWidth: "800px",
        maxHeight: "90vh",
        overflow: "hidden",
        boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)",
        display: "flex",
        flexDirection: "column",
    };

    const headerStyle: React.CSSProperties = {
        padding: "24px",
        borderBottom: "1px solid #e5e7eb",
        backgroundColor: "#f9fafb",
    };

    const titleStyle: React.CSSProperties = {
        fontSize: "20px",
        fontWeight: "600",
        color: "#1f2937",
        margin: 0,
        marginBottom: "8px",
    };

    const subtitleStyle: React.CSSProperties = {
        fontSize: "14px",
        color: "#6b7280",
        margin: 0,
    };

    const contentStyle: React.CSSProperties = {
        flex: 1,
        overflow: "auto",
        padding: "24px",
    };

    const masterControlStyle: React.CSSProperties = {
        display: "flex",
        alignItems: "center",
        gap: "12px",
        marginBottom: "24px",
        padding: "16px",
        backgroundColor: "#f3f4f6",
        borderRadius: "8px",
        border: "1px solid #d1d5db",
    };

    const toggleStyle: React.CSSProperties = {
        position: "relative",
        width: "64px", // Wider for tristate
        height: "24px",
        backgroundColor: getMasterToggleColors().bg,
        borderRadius: "12px",
        cursor: "pointer",
        transition: "all 0.2s",
    };

    const toggleKnobStyle: React.CSSProperties = {
        position: "absolute",
        top: "2px",
        left: getMasterToggleColors().knobPos,
        width: "20px",
        height: "20px",
        backgroundColor: "white",
        borderRadius: "50%",
        transition: "all 0.2s",
        boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
    };

    const mealListStyle: React.CSSProperties = {
        maxHeight: "400px",
        overflow: "auto",
        border: "1px solid #e5e7eb",
        borderRadius: "8px",
        backgroundColor: "white",
    };

    const mealItemStyle: React.CSSProperties = {
        display: "flex",
        alignItems: "center",
        padding: "12px 16px",
        borderBottom: "1px solid #f3f4f6",
        gap: "12px",
    };

    const mealInfoStyle: React.CSSProperties = {
        flex: 1,
        minWidth: 0,
    };

    const mealNameStyle: React.CSSProperties = {
        fontWeight: "500",
        fontSize: "14px",
        color: "#1f2937",
        marginBottom: "2px",
    };

    const mealTypeStyle: React.CSSProperties = {
        fontSize: "12px",
        color: "#6b7280",
    };

    const notesInputStyle: React.CSSProperties = {
        width: "200px",
        padding: "6px 8px",
        border: "1px solid #d1d5db",
        borderRadius: "4px",
        fontSize: "12px",
        outline: "none",
    };

    const overallNotesStyle: React.CSSProperties = {
        marginTop: "24px",
    };

    const labelStyle: React.CSSProperties = {
        display: "block",
        fontSize: "14px",
        fontWeight: "500",
        color: "#374151",
        marginBottom: "8px",
    };

    const textareaStyle: React.CSSProperties = {
        width: "100%",
        minHeight: "80px",
        padding: "12px",
        border: "1px solid #d1d5db",
        borderRadius: "6px",
        fontSize: "14px",
        resize: "vertical",
        outline: "none",
    };

    const footerStyle: React.CSSProperties = {
        padding: "20px 24px",
        borderTop: "1px solid #e5e7eb",
        backgroundColor: "#f9fafb",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
    };

    const cancelButtonStyle: React.CSSProperties = {
        padding: "10px 20px",
        border: "1px solid #d1d5db",
        borderRadius: "6px",
        backgroundColor: "white",
        color: "#374151",
        cursor: "pointer",
        fontSize: "14px",
        fontWeight: "500",
    };

    const submitButtonStyle: React.CSSProperties = {
        padding: "10px 20px",
        border: "none",
        borderRadius: "6px",
        backgroundColor: "#3b82f6",
        color: "white",
        cursor: "pointer",
        fontSize: "14px",
        fontWeight: "500",
    };

    return (
        <div style={overlayStyle} onClick={onClose}>
            <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
                <div style={headerStyle}>
                    <h3 style={titleStyle}>Meal Plan Preferences</h3>
                    <p style={subtitleStyle}>
                        Set your preferences for meals from "{mealPlan?.name}"
                        to guide the new meal plan generation
                    </p>
                </div>

                <div style={contentStyle}>
                    {/* Master Control */}
                    <div style={masterControlStyle}>
                        <div style={toggleStyle} onClick={handleMasterToggle}>
                            <div style={toggleKnobStyle} />
                        </div>
                        <span style={{ fontWeight: "500", color: "#374151" }}>
                            {getMasterToggleLabel()} ({meals.length} meals)
                        </span>
                        <span
                            style={{
                                fontSize: "12px",
                                color: "#6b7280",
                                marginLeft: "auto",
                            }}
                        >
                            👎 Dislike • 😐 Ignore • 👍 Like
                        </span>
                    </div>

                    {/* Meals List */}
                    <div style={mealListStyle}>
                        {meals.map((meal) => {
                            const mealColors = getToggleColors(meal.preference);
                            return (
                                <div key={meal.id} style={mealItemStyle}>
                                    <div
                                        style={{
                                            position: "relative",
                                            width: "64px",
                                            height: "20px",
                                            backgroundColor: mealColors.bg,
                                            borderRadius: "10px",
                                            cursor: "pointer",
                                            transition: "all 0.2s",
                                        }}
                                        onClick={() =>
                                            handleMealToggle(meal.id)
                                        }
                                    >
                                        <div
                                            style={{
                                                position: "absolute",
                                                top: "2px",
                                                left: mealColors.knobPos,
                                                width: "16px",
                                                height: "16px",
                                                backgroundColor: "white",
                                                borderRadius: "50%",
                                                transition: "all 0.2s",
                                                boxShadow:
                                                    "0 2px 4px rgba(0, 0, 0, 0.1)",
                                            }}
                                        />
                                    </div>

                                    <div style={mealInfoStyle}>
                                        <div style={mealNameStyle}>
                                            {meal.recipeName}
                                        </div>
                                        <div style={mealTypeStyle}>
                                            Day {meal.dayNumber} •{" "}
                                            {meal.mealType}
                                        </div>
                                    </div>

                                    <input
                                        type="text"
                                        placeholder="Optional notes..."
                                        value={meal.notes}
                                        onChange={(e) =>
                                            handleMealNotesChange(
                                                meal.id,
                                                e.target.value
                                            )
                                        }
                                        style={{
                                            ...notesInputStyle,
                                            opacity:
                                                meal.preference === "ignore"
                                                    ? 0.5
                                                    : 1,
                                        }}
                                        disabled={meal.preference === "ignore"}
                                    />
                                </div>
                            );
                        })}
                    </div>

                    {/* Overall Notes */}
                    <div style={overallNotesStyle}>
                        <label style={labelStyle}>
                            Additional Preferences & Notes
                        </label>
                        <textarea
                            value={overallNotes}
                            onChange={(e) => setOverallNotes(e.target.value)}
                            placeholder="Any additional dietary preferences, restrictions, or goals for the new meal plan..."
                            style={textareaStyle}
                        />
                    </div>
                </div>

                <div style={footerStyle}>
                    <button onClick={onClose} style={cancelButtonStyle}>
                        Cancel
                    </button>
                    <button onClick={handleSubmit} style={submitButtonStyle}>
                        Generate With Preferences
                    </button>
                </div>
            </div>
        </div>
    );
};
